/**
 * 考試範圍複習(P4)—— 選某次考試 → 撈該日期範圍內的錯題 → 生成那次的複習卷。
 * Source of truth: docs/spec/EXAM_PLANNER_SPEC.md §4(P4)。
 *
 * 把「考試規劃(日期範圍)」接到既有「個人化複習卷」:
 *   · 過濾 progress.mistakes:日期(timestamp→本地 YYYY-MM-DD)落在該次考試 window 內;
 *     小考再按 window.event.subject 過濾(英文小考只撈英文錯題)。
 *   · 用「過濾後的 progress 副本」呼叫 runReviewPaper(只決定組哪些題,不存回去)。
 * 範圍內沒有錯題 → 友善提示。
 *
 * 學習/遊戲分離鐵則:純讀學習側資料(mistakes),不碰戰鬥數值。視覺=學術風格。
 */
import { loadProgress } from '../../shared/progress';
import type { PlayerProgress, MistakeEntry } from '../../shared/types';
import type { ExamWindow } from './types';
import { EXAM_TYPE_LABEL, SUBJECT_LABEL } from './types';
import { windowContains } from './scope';
import { runReviewPaper } from '../review/review-paper';
import { runExamPlanner } from './planner-view';
import { pageShell, academicButton } from '../ui/components';

/** Unix ms → 本地 'YYYY-MM-DD'(與考試日期同格式,可直接字典序比較)。 */
function tsToLocalDate(ts: number): string {
  const d = new Date(ts);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * 過濾出「屬於某次考試範圍」的錯題:日期落在 window 內;小考再按 window.event.subject
 * 過濾(英文小考只撈英文錯題;段考全科不過濾)。純函式,供 runExamReview + 測試共用。
 */
export function scopeMistakesToExam(mistakes: MistakeEntry[], window: ExamWindow): MistakeEntry[] {
  return mistakes.filter((m) => {
    if (window.event.subject && m.subject !== window.event.subject) return false;
    return windowContains(window, tsToLocalDate(m.timestamp));
  });
}

/** 某次考試的顯示名(小考帶科目)。 */
function examLabel(w: ExamWindow): string {
  const e = w.event;
  if (e.name?.trim()) return e.name.trim();
  if (e.type === 'quiz' && e.subject) return `${SUBJECT_LABEL[e.subject]}小考`;
  return EXAM_TYPE_LABEL[e.type];
}

/**
 * 複習某一次考試:撈範圍內錯題 → 生成複習卷。範圍內無錯題 → 提示。
 * `onDone` 回到呼叫端(規劃表)。
 */
export async function runExamReview(
  container: HTMLElement,
  window: ExamWindow,
  onDone: () => void,
): Promise<void> {
  const progress = loadProgress();
  const label = examLabel(window);

  const scoped = scopeMistakesToExam(progress?.mistakes ?? [], window);

  if (!progress || scoped.length === 0) {
    renderEmptyScope(container, label, onDone);
    return;
  }

  // 過濾後的 progress 副本:只留範圍內錯題;studentKPs 清空,避免把不在範圍的 box
  // 弱點也拉進這張「考試範圍卷」。runReviewPaper 只用它組題,時鐘/存檔仍動真 progress。
  const scopedProgress: PlayerProgress = { ...progress, mistakes: scoped, studentKPs: [] };
  await runReviewPaper(container, onDone, { buildFrom: scopedProgress });
}

/** 範圍內沒有錯題的友善提示(學術風)。 */
function renderEmptyScope(container: HTMLElement, label: string, onDone: () => void): void {
  container.innerHTML = '';
  const shell = pageShell(container, {
    title: `複習 ${label}`,
    subtitle: '這次考試的日期範圍內還沒有錯題可複習。',
    onBack: onDone,
  });
  const p = document.createElement('p');
  p.style.cssText = 'color:var(--ef-sub);font-size:15px;line-height:1.8;';
  p.textContent =
    '錯題會在你「拍照上傳考卷(自動批改)」或「答練習卷答錯」時自動進錯題本,' +
    '並依日期歸到對應的那次考試。先去拍一張這次範圍的考卷上傳,這裡就會有題目可複習。';
  shell.body.appendChild(p);

  const back = academicButton({ label: '← 回考試規劃', variant: 'secondary', onClick: onDone });
  back.style.marginTop = '18px';
  shell.body.appendChild(back);
}

/**
 * 考試規劃入口(含複習導覽):開規劃表,點某次「複習這次」→ 進 runExamReview,
 * 複習結束再回規劃表。review-hub 直接呼這個。
 */
export function openExamPlanner(container: HTMLElement, onBack: () => void): void {
  const reopen = (): void => {
    container.innerHTML = '';
    runExamPlanner(container, {
      onBack,
      onReviewExam: (window) => {
        container.innerHTML = '';
        void runExamReview(container, window, reopen);
      },
    });
  };
  reopen();
}
